import React, { useState } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { closeModal } from 'reducers/modals';
// components
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import Modal from 'components/Modal';
import Button from 'components/Button';
import Input from 'components/Input';
import ToggleSwitcher from 'components/ToggleSwitcher';
import SingleDatePicker from 'components/DatePicker/SingleDatePicker/SingleDatePicker';
// endpoints
import { closeBinCycle } from 'endpoints/feedDeliveries';
// utils
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { displayToLbs, weightUnit } from '../utils';

const CloseBinModal = ({ farmId, bin, measurementSystem, onSuccess, closeModal }) => {
  const [closedOn, setClosedOn] = useState(moment());
  // "Is the bin empty?" defaults to Yes - one tap for the common case
  const [isBinEmpty, setIsBinEmpty] = useState(true);
  const [remaining, setRemaining] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      await closeBinCycle(farmId, bin.id, bin.current_cycle.id, {
        closed_on: closedOn.format('YYYY-MM-DD'),
        remaining_at_close: isBinEmpty ? 0 : displayToLbs(remaining, measurementSystem),
      });
      closeModal();
      onSuccess();
    } catch (error) {
      toastResponseErrors(error);
    } finally {
      setIsSaving(false);
    }
  };

  const isValid = closedOn && (isBinEmpty || Number(remaining) >= 0);

  return (
    <Modal
      title={<FormattedMessage id="component.feedDeliveries.closeBinTitle" values={{ binName: bin.name }} />}
      className="BinCycleModal"
    >
      <section className="modal-body">
        <div className="form-row">
          <span className="input-label"><FormattedMessage id="component.feedDeliveries.binCloseDate" /></span>
          <SingleDatePicker
            inputId="binCloseDateInput"
            className="date-input"
            date={closedOn}
            onDateChange={setClosedOn}
            isOutsideRange={(day) => day.isAfter(moment(), 'day')
              || day.isBefore(moment(bin.current_cycle.opened_on), 'day')}
          />
        </div>

        <div className="form-row">
          <ToggleSwitcher
            name="is_bin_empty"
            text={<FormattedMessage id="component.feedDeliveries.isBinEmpty" />}
            checked={isBinEmpty}
            onChange={(e) => setIsBinEmpty(e.target.checked)}
          />
        </div>

        {!isBinEmpty && (
          <div className="form-row">
            <span className="input-label">
              <FormattedMessage id="component.feedDeliveries.remainingAtClose" /> ({weightUnit(measurementSystem)})
            </span>
            <Input
              type="number"
              value={remaining}
              onChange={(e) => setRemaining(e.target.value)}
              min={0}
              step={0.01}
              required
            />
          </div>
        )}
      </section>

      <section className="modal-footer text-right">
        <Button className="margin-right-1" onClick={closeModal} disabled={isSaving}>
          <FormattedMessage id="general.button.cancel" />
        </Button>
        <Button primary onClick={handleSubmit} disabled={isSaving || !isValid || (!isBinEmpty && remaining === '')}>
          <FormattedMessage id="component.feedDeliveries.closeBin" />
        </Button>
      </section>
    </Modal>
  );
};

CloseBinModal.propTypes = {
  farmId: T.number.isRequired,
  bin: T.object.isRequired,
  measurementSystem: T.oneOf(['imperial', 'metric']),
  onSuccess: T.func.isRequired,
  closeModal: T.func.isRequired,
};

export default connect(null, { closeModal })(CloseBinModal);
